// novem atom — token-styled native <select> (NovemHR-ekranlar port).
//
// The B3/B4 modal forms styled a bare <select> inline (`selectClass` in
// licenses.tsx etc.). This promotes that to a real component so the
// chrome (border, radius, focus ring, disabled wash, chevron affordance)
// lives in the skin and the call sites stop re-declaring it.
//
// Native <select> on purpose: it's the most accessible, keyboard- and
// mobile-friendly single-choice control. We do NOT reinvent it as a
// listbox — the consumer supplies <option> children.
//
// Mirrors the Input conventions:
//   * size: sm / md / lg (data-size hook → skin sizing)
//   * error=true → aria-invalid; the consumer binds aria-describedby to
//     its own error element (or wraps in FormField which wires it).
//   * Label association is the consumer's job (aria-label, or htmlFor via
//     FormField). The control itself just forwards the ref + props.

import { forwardRef, type SelectHTMLAttributes } from "react";

import { cx } from "../lib/cx.ts";

export type SelectSize = "sm" | "md" | "lg";

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, "size"> {
  readonly size?: SelectSize;
  readonly error?: boolean;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ size = "md", error = false, disabled, className, children, ...rest }, ref) => (
    <select
      ref={ref}
      data-component="select"
      data-size={size}
      data-error={error || undefined}
      aria-invalid={error || undefined}
      aria-disabled={disabled === true ? true : undefined}
      disabled={disabled === true}
      className={cx("nai-select", className)}
      {...rest}
    >
      {children}
    </select>
  ),
);
Select.displayName = "Select";
